import { QuestionGroupType } from '@exam-management/graphql';

import type {
  QSetQuestion,
  Question,
  QuestionFileName,
  QuestionGroupFileName,
  QuestionWithSnapshot,
} from './types';

// Consider promote to a shared utils
export function updateImageTags(content: string, urlObject: Record<string, string>) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  const images = doc.getElementsByTagName('img');

  for (const img of Array.from(images)) {
    const contentAttr = img.getAttribute('content');
    if (contentAttr && urlObject[contentAttr]) {
      img.setAttribute('src', urlObject[contentAttr]);
    }
  }

  return doc.body.innerHTML;
}

// Consider promote to a shared utils
function extractImagesFileNames(content: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  const images = doc.getElementsByTagName('img');

  const filenames: string[] = [];

  for (const img of Array.from(images)) {
    const contentAttr = img.getAttribute('content');
    if (contentAttr) {
      filenames.push(contentAttr);
    }
  }

  return [...new Set(filenames)]; // Remove duplicates
}

function getQuestionFileNames(question: Question | QuestionWithSnapshot) {
  const questionFilenames: string[] = [];
  const questionGroupFilenames: string[] = [];

  questionFilenames.push(...extractImagesFileNames(question.details));
  questionFilenames.push(...extractImagesFileNames(question.solution?.value ?? ''));
  questionFilenames.push(...extractImagesFileNames(question.metadata?.reference ?? ''));

  if (question.questionGroup?.type === QuestionGroupType.ItemSet) {
    questionGroupFilenames.push(
      ...extractImagesFileNames(question.questionGroup?.metadata?.paragraph ?? ''),
    );
  }

  for (const id in question.metadata.choices) {
    const choiceLabel = question.metadata.choices[id].label;
    questionFilenames.push(...extractImagesFileNames(choiceLabel));
  }

  return {
    questions: questionFilenames,
    questionGroups: questionGroupFilenames,
  };
}

function extractFileNames<
  TQSetQuestion extends QSetQuestion,
  UQuestion extends Question | QuestionWithSnapshot,
>(
  qSetQuestions: TQSetQuestion[],
  questionAccessor: (qSetQuestion: TQSetQuestion) => UQuestion | null | undefined,
) {
  return qSetQuestions.reduce(
    (acc, qSetQuestion) => {
      const question = questionAccessor(qSetQuestion);

      if (!question) return acc;

      const { questions: questionsFilenames, questionGroups: questionGroupsFilenames } =
        getQuestionFileNames(question);

      if (questionsFilenames.length === 0 && questionGroupsFilenames.length === 0) {
        return acc;
      }

      acc.questions.push({
        questionId: qSetQuestion.questionId,
        fileNames: questionsFilenames,
      });

      if (
        question.questionGroup &&
        question.questionGroup.type === `${QuestionGroupType.ItemSet}`
      ) {
        acc.questionGroups.push({
          id: qSetQuestion.questionGroupId,
          fileNames: questionGroupsFilenames,
        });
      }

      return acc;
    },
    {
      questions: [] as QuestionFileName[],
      questionGroups: [] as QuestionGroupFileName[],
    },
  );
}

export function getQSetQuestionsFileNames(qSetQuestions: QSetQuestion[]) {
  return extractFileNames(qSetQuestions, (qSetQuestion) => qSetQuestion.question);
}

export function getQSetQuestionsSnapshotFileNames(qSetQuestions: QSetQuestion[]) {
  return extractFileNames(qSetQuestions, (qSetQuestion) => qSetQuestion.questionSnapshot);
}
